import React, { useState } from "react";
import { Box, Typography, Chip, useMediaQuery, useTheme } from "@mui/material";
import {
  ProcessedUltrasoundImage,
  UltrasoundImage,
} from "../../../utility/interfaces/patientMasterDataInterface";
import { parseUltrasoundImages } from "../../patient-dashboard/services/ParseUltrasoundImages";
import { Pagination } from "swiper/modules";
import FullScreenGallery from "./FullScreenGallery";
import styles from "./LatestUltrasoundGroup.module.scss";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import CustomSwiper from "../../../components/v2/custom-swiper-layout/CustomSwiper";
import { useTranslation } from "react-i18next";

interface LatestUltrasoundGroupProps {
  imageDate: string;
  imageCount: number;
  ultrasoundImages: UltrasoundImage[];
  preferredLanguage: string;
}

const LatestUltrasoundGroup: React.FC<LatestUltrasoundGroupProps> = ({
  imageDate,
  imageCount,
  ultrasoundImages,
  preferredLanguage,
}) => {
  const isMobile = useMediaQuery("(max-width: 900px)");
  const { t } = useTranslation();

  const ultrasoundImageData: ProcessedUltrasoundImage[] = parseUltrasoundImages(
    ultrasoundImages,
    imageDate,
    preferredLanguage
  );

  const theme = useTheme();
  const { values } = theme.breakpoints;
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const [startIndex, setStartIndex] = useState(0);

  const openGallery = (index: number) => {
    setStartIndex(index);
    setIsGalleryOpen(true);
  };

  return (
    <>
      { ultrasoundImageData?.length > 0 && (
        <Box className={styles.container}>
          <Box
            className={`${styles.contentBox} ${isMobile ? styles.mobile : ""}`}
          >
            <Chip
              label={t("ultrasoundPage.newestAvailable")}
              className={styles.newestUltrasound}
            />

            <Typography variant="h3" className={styles.heading} gutterBottom>
              {ultrasoundImageData[0]?.date}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {imageCount} {imageCount > 1 ? t("images") : t("image")}
            </Typography>

            <Box className={styles.imageCarouselContainer}>
              <CustomSwiper
                items={ultrasoundImageData}
                modules={[Pagination]}
                swiperOptions={{
                  navigation: true,
                  pagination: !isMobile
                    ? {
                        clickable: true,
                        el: `.${styles.ultrasoundSwiperPagination}`,
                      }
                    : false, // Disable pagination on mobile,
                  slidesPerView: 2,
                  centeredSlides: false,
                  centerInsufficientSlides: true,
                  spaceBetween: 2,
                  breakpoints: {
                    [values.xs]: {
                      slidesPerView: 1.2,
                      spaceBetween: 4,
                    },
                    [values.sm]: {
                      slidesPerView: 1.8,
                      spaceBetween: 4,
                    },
                    [values.md]: {
                      slidesPerView: 2.8,
                      spaceBetween: 8,
                    },
                    [values.lg]: {
                      slidesPerView: 4,
                      spaceBetween: 8,
                    },
                    [values.xl]: {
                      slidesPerView: 4,
                      spaceBetween: 10,
                    },
                  },
                }}
                onSlideClick={(index) => openGallery(index)}
                renderSlide={(img) => (
                  <div>
                    <img
                      src={img.imageUrl}
                      alt={img.imageName}
                      className={styles.carouselImage}
                    />
                  </div>
                )}
              />

              {!isMobile && (
                <div className={styles.ultrasoundSwiperPagination} />
              )}
            </Box>

            <FullScreenGallery
              images={ultrasoundImageData}
              open={isGalleryOpen}
              initialIndex={startIndex}
              onClose={() => setIsGalleryOpen(false)}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default LatestUltrasoundGroup;

